import * as _4 from 'lodash4';
import { IPlanFeedbackTerm } from '../../suggestions/components/plan_feedback/builders/plan_feedback_term_builder';
import { APCRGenericSlot, APGenericSlot, CourseSlot } from '../builders/ap_generic_slot_builder';
import {
  APProgramSlot,
  CourseGroupProgramSlot,
  IAPProgramSlotMeta,
  NCRProgramSlot,
  SingleCourseProgramSlot,
  WildcardProgramSlot
} from '../builders/ap_program_slot_builder';
import { IAPAPMarkAsMetStatusDatasource } from '../components/mark_as_met/datasources/mark_as_met_status_datasource';
import { IAPProgramSlotDatasource } from '../datasources/ap_program_slot_datasource';
import { APGenericSlotServant } from './ap_generic_slot_servant';

export interface IProgramSlot {
  _selectedCourses: string;
  courses: any[];
  id: number;
  course_group: string;
  _courses: any[];
  _is_elective: boolean;
  _is_course_group: boolean;
  _plannedSingleCourseProgramSlot: boolean; // Indicates that a program slot only has one course and is planned
}

export interface ICourse {
  course_id: number;
  _course_group: string;
  _course: any;
  _is_group_course: boolean;
  _program_slot: number;
}

export interface IYearSlot {
  _terms: any[];
}

export class APProgramSlotServant {
  static updateCourseGroupPlannedState(datasource: IAPProgramSlotDatasource, id: number) {
    const record = datasource.get(id);
    if (record && record.slot_type === 'GROUP') {
      record.is_course_group_planned = !record.is_course_group_planned;
      datasource.put(record);
    }
    return datasource;
  }

  static isCourseGroupProgramSlot(pgmSlot: APProgramSlot): pgmSlot is CourseGroupProgramSlot {
    return pgmSlot && pgmSlot.slot_type === 'GROUP';
  }

  static isProgramSlotMET(pgmSlot: APProgramSlot) {
    if (!pgmSlot) {
      return false;
    }
    return pgmSlot.status === 'MET';
  }

  static getCourseGroupProgramSlots(pgmSlots: APProgramSlot[]) {
    return pgmSlots.filter((pgmSlot): pgmSlot is CourseGroupProgramSlot & IAPProgramSlotMeta =>
      APProgramSlotServant.isCourseGroupProgramSlot(pgmSlot)
    );
  }

  static getCourseRelatedProgramSlots(pgmSlots: APProgramSlot[]) {
    return pgmSlots.filter(
      (pgmSlot): pgmSlot is APCRGenericSlot['programSlot'] => !APProgramSlotServant.isNCRProgramSlot(pgmSlot)
    );
  }

  static isWildcardProgramSlot(pgmSlot: APProgramSlot): pgmSlot is WildcardProgramSlot {
    return pgmSlot.slot_type === 'WILDCARD';
  }
  static isNCRProgramSlot(pgmSlot: APProgramSlot): pgmSlot is NCRProgramSlot {
    return pgmSlot.slot_type === 'NCR';
  }

  static getWildCardProgramSlots(pgmSlots: APProgramSlot[]) {
    return pgmSlots.filter((pgmSlot): pgmSlot is WildcardProgramSlot & IAPProgramSlotMeta =>
      APProgramSlotServant.isWildcardProgramSlot(pgmSlot)
    );
  }

  static isCourseProgramSlot(pgmSlot: APProgramSlot): pgmSlot is SingleCourseProgramSlot {
    return pgmSlot.slot_type === 'COURSE';
  }

  static getMinCredits(slots: APGenericSlot[]): number {
    const totalMinCredits = _4.sumBy(slots, (slot: APGenericSlot & IPlanFeedbackTerm) =>
      slot.diff !== 'REMOVE' ? _4.get(slot, 'min_credits', 0) : 0
    );
    return totalMinCredits ? totalMinCredits : 0;
  }

  static getMaxCredits(slots: APGenericSlot[]): number {
    const totalMaxCredits = _4.sumBy(slots, (slot: APGenericSlot & IPlanFeedbackTerm) =>
      slot.diff !== 'REMOVE' ? _4.get(slot, 'max_credits', 0) : 0
    );
    return totalMaxCredits ? totalMaxCredits : 0;
  }

  static getCreditsForGenericSlots(slots: APGenericSlot[]): number {
    const slotsWithCredits = _4.filter(slots, (genericSlot) => _4.get(genericSlot, 'credits'));
    return _4.reduce(
      slotsWithCredits,
      (sum: number, s: CourseSlot & IPlanFeedbackTerm) => {
        return s.diff !== 'REMOVE' ? sum + s.credits : 0;
      },
      0
    );
  }

  static hasCreditRangeExceed(slots: APGenericSlot[], recommendedCreditCap: number): boolean {
    const totalMinCredits = APProgramSlotServant.getMinCredits(slots);
    const courseSlots = APGenericSlotServant.getCourseSlots(slots);
    const credits = APProgramSlotServant.getCreditsForGenericSlots(courseSlots);
    return credits + totalMinCredits > recommendedCreditCap;
  }

  static getCreditsRange(slots: APGenericSlot[]): string {
    const totalMinCredits = APProgramSlotServant.getMinCredits(slots);
    const totalMaxCredits = APProgramSlotServant.getMaxCredits(slots);
    const courseSlots = APGenericSlotServant.getCourseSlots(slots);
    const wildcardSlots = APGenericSlotServant.getWildCardSlots(slots);
    const credits = APProgramSlotServant.getCreditsForGenericSlots([...courseSlots, ...wildcardSlots]);

    return totalMinCredits !== totalMaxCredits
      ? `${credits + totalMinCredits} - ${credits + totalMaxCredits}`
      : `${totalMaxCredits + credits}`;
  }
}



// WEBPACK FOOTER //
// ./app/vendor/capability-ng-academic-planner/src/global/servants/ap_program_slot_servant.ts